<template>
  <div class="page">
    <PageHeader :to="{ name: 'group-notifications' }">
      <div slot="middle">{{ isEditing ? $t('Edit number') : $t('Add number') }}</div>
    </PageHeader>
    <div class="px-8 pt-8 content-section">
      <v-text-field
        :label="$t('Phone number')"
        outlined
        v-model="form.phone"
        class="col-6"
        :rules="rules.phone"
      ></v-text-field>

      <p class="mb-1">{{ $t('Plan type') }}</p>
      <div class="mb-2">
        <v-btn :outlined="scheduleType !== 'simple'" class="mr-2" color="primary"
               @click="scheduleType = 'simple'">{{ $t('Simple') }}</v-btn>
        <v-btn :outlined="scheduleType !== 'advanced'" color="primary"
               @click="scheduleType = 'advanced'">{{ $t('Advanced') }}</v-btn>
      </div>

      <!-- Simple scheduleType -->
      <div v-if="scheduleType === 'simple'">
        <div class="plan-explainer">
          <p>
            {{ $t('Simple plan explainer') }}
          </p>
        </div>

        <div class="mb-8">
          <div>{{ $t('Select days') }}</div>

          <div class="d-flex">
            <div
              v-for="key in Object.keys(planSpecificDays)"
              :key="key"
              class="frequency-item"
              :class="{ 'frequency-item-selected' : planSpecificDays[key] }"
              @click="planSpecificDays[key] = !planSpecificDays[key]"
            >{{ $t(key) }}</div>
          </div>
        </div>

        <div class="mb-2">
          <div>{{ $t('Choose time') }}</div>

          <div class="d-flex">
            <VTextField
              :label="$t('From')"
              outlined
              v-model="times.start"
              v-mask="'##:##'"
              style="max-width: 100px !important;"
              class="mr-2"
            />
            <VTextField
              :label="$t('To')"
              outlined
              v-model="times.end"
              v-mask="'##:##'"
              style="max-width: 100px !important;"
            />
          </div>
        </div>

        <div class="mb-8">
          <DayCalendar
            class="day-calendar"
            :tasks="taskPreview"
            display-only
            scale-up
            scale-down
          />
        </div>

        <v-btn color="primary" :disabled="!canSubmit" @click="createAlarm">{{ isEditing ? $t('Save changes') : $t('Create') }}</v-btn>
      </div>

      <!-- Advanced scheduleType -->
      <div v-if="scheduleType === 'advanced'">
        <div class="plan-explainer">
          <p>
            {{ $t('Advanced plan explainer') }}
          </p>
        </div>

        <div v-for="(adv, i) in advancedTimes" :key="adv.day" class="mb-2">
          <div class="advanced-day" :class="{ 'advanced-day--active': adv.times.length }"><span style="display:inline-block; width:80px">{{ $t(adv.day) }}</span>
            <v-btn v-if="!adv.times.length" class="advanced-times-add" x-small @click="addTimeToDay(adv.day)">+</v-btn>
          </div>
          <div v-if="adv.times.length" class="advanced-times-container">
            <div class="advanced-times-text">{{ $t('Times') }}:</div>
            <div class="d-flex" v-for="(time, timeIndex) in adv.times" :key="timeIndex">
              <VTextField
                :label="$t('From')"
                outlined
                v-model="time.start"
                v-mask="'##:##'"
                style="max-width: 100px !important;"
                class="mr-1 textfield-small"
                persistent-placeholder
                @blur="checkTimeDelete(i, timeIndex)"
              />
              <VTextField
                :label="$t('To')"
                outlined
                v-model="time.end"
                v-mask="'##:##'"
                style="max-width: 100px !important;"
                class="textfield-small"
                persistent-placeholder
                @blur="checkTimeDelete(i, timeIndex)"
              />
            </div>
            <v-btn class="advanced-times-add" x-small @click="addTimeToDay(adv.day)">+</v-btn>
          </div>
          <DayCalendar
            v-if="adv.times.length"
            class="day-calendar"
            :tasks="taskPreview[i]"
            display-only
            scale-up
            scale-down
          />
        </div>
        <v-btn color="primary" class="mb-4" :disabled="!canSubmit" @click="createAlarm">{{ isEditing ? $t('Save changes') : $t('Create') }}</v-btn>
      </div>
    </div>
  </div>
</template>
<script>
import PageHeader from '@/globals/components/PageHeader.vue';
import DayCalendar from '@/modules/calendar/DayCalendar.vue';
import {
  setHours, setMinutes, setSeconds, getUnixTime, format,
  setDay, getHours, getMinutes, getDay, fromUnixTime,
} from 'date-fns';
import { mask } from 'vue-the-mask';
import { parsePhoneNumber } from 'libphonenumber-js';

const mappedDays = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

export default {
  name: 'group-notifications-edit',
  components: { DayCalendar, PageHeader },
  directives: {
    mask,
  },
  data() {
    return {
      scheduleType: 'simple',
      times: { start: '', end: '' },
      advancedTimes: [
        { day: 'Monday', times: [] },
        { day: 'Tuesday', times: [] },
        { day: 'Wednesday', times: [] },
        { day: 'Thursday', times: [] },
        { day: 'Friday', times: [] },
        { day: 'Saturday', times: [] },
        { day: 'Sunday', times: [] },
      ],
      planSpecificDays: {
        Monday: false,
        Tuesday: false,
        Wednesday: false,
        Thursday: false,
        Friday: false,
        Saturday: false,
        Sunday: false,
      },
      form: {
        phone: '',
        tasks: [],
        enabled: true,
        schedule: {
          intervals: [],
        },
      },
      rules: {
        phone: [
          (v) => !!v || this.$t('Phone number is required'),
          () => this.phoneNumberIsValid || this.$t('Phone number is invalid'),
        ],
      },
    };
  },
  computed: {
    canSubmit() {
      if (this.scheduleType === 'simple') {
        return this.phoneNumberIsValid && this.times.start && this.times.end && Object.values(this.planSpecificDays).some((day) => day);
      }
      return this.phoneNumberIsValid && this.advancedTimes.some((day) => day.times.length);
    },
    phoneNumberIsValid() {
      try {
        return parsePhoneNumber(this.form.phone, 'DK').isValid();
      } catch {
        return false;
      }
    },
    isEditing() {
      return !!this.$route.params.id;
    },
    groupName() {
      return this.$route.params.groupName;
    },
    currentTime() {
      return Date.now();
    },
    taskPreview() {
      if (this.scheduleType === 'simple') {
        return [{
          zones: this.generatePreview(this.times, this.currentTime),
        }];
      }
      const preview = [];
      this.advancedTimes.forEach((day) => {
        const arr = [{ zones: [] }];
        day.times.forEach((time) => {
          const generated = this.generatePreview(time, this.currentTime);
          if (generated) {
            arr[0].zones.push(this.generatePreview(time, this.currentTime)[0]);
          }
        });
        preview.push(arr);
      });
      return preview;
    },
  },
  mounted() {
    if (this.$route.params.id) {
      this.$Alert.api().fetchByGroupAndId(this.$store.state.selectedGroup, this.$route.params.id).then(() => {
        this.setAlertDefault(this.$Alert.find(this.$route.params.id));
      });
    }
  },
  methods: {
    checkTimeDelete(dayIndex, timeIndex) {
      const time = this.advancedTimes[dayIndex].times[timeIndex];
      if (this.isEmptyOrUndefined(time.start) && this.isEmptyOrUndefined(time.end)) {
        this.advancedTimes[dayIndex].times.splice(timeIndex, 1);
      }
    },
    addTimeToDay(day) {
      const targetDay = this.advancedTimes.find((d) => d.day === day);
      targetDay.times.push({ start: '00:00', end: '00:00' });
    },
    generatePreview(times, day) {
      const [startHours, startMinutes] = times.start.split(':');
      const [endHours, endMinutes] = times.end.split(':');
      if (this.isEmptyOrUndefined(startHours)
        || this.isEmptyOrUndefined(startMinutes)
        || this.isEmptyOrUndefined(endHours)
        || this.isEmptyOrUndefined(endMinutes)) {
        return null;
      }

      return [{
        fill: '#0dac30',
        startTime:
          setSeconds(setMinutes(setHours(day, startHours), startMinutes), 0),
        endTime:
          setSeconds(setMinutes(setHours(day, endHours), endMinutes), 0),
      }];
    },
    setAlertDefault(alert) {
      this.form.phone = alert.phoneNumber;
      this.form.enabled = alert.enabled;
      this.scheduleType = this.determineScheduleType(alert.schedule.intervals);
      if (this.scheduleType === 'simple') {
        this.planSpecificDays = this.generatePlanSpecificDays(alert.schedule.intervals);
        // eslint-disable-next-line prefer-destructuring
        const generatedTime = this.generateTimesFromIntervals(alert.schedule.intervals);
        this.times = generatedTime[0] ? generatedTime[0] : { start: '', end: '' };
      } else {
        alert.schedule.intervals.forEach((interval) => {
          const day = getDay(fromUnixTime(interval.timestamp));
          const targetDay = Object.entries(mappedDays).find(([, value]) => value === day)[0];
          const time = this.generateTimesFromIntervals([interval])[0];
          this.advancedTimes.find((d) => d.day === targetDay).times.push(time);
        });
      }
    },
    generateTimesFromIntervals(intervals) {
      return intervals.map((interval) => {
        const start = this.getStartTime(interval);
        const end = this.getEndTime(interval);
        return { start, end };
      });
    },
    generatePlanSpecificDays(intervals) {
      const days = {
        Monday: false,
        Tuesday: false,
        Wednesday: false,
        Thursday: false,
        Friday: false,
        Saturday: false,
        Sunday: false,
      };
      intervals.forEach((interval) => {
        const day = getDay(fromUnixTime(interval.timestamp));
        const targetDay = Object.entries(mappedDays).find(([, value]) => value === day)[0];
        days[targetDay] = true;
      });
      return days;
    },
    determineScheduleType(intervals) {
      const result = intervals.reduce((acc, cur) => {
        if (acc === null) {
          return cur;
        }
        if (acc === false) {
          return false;
        }
        const dateCur = fromUnixTime(cur.timestamp);
        const dateAcc = fromUnixTime(acc.timestamp);
        const hourMatch = getHours(dateCur) === getHours(dateAcc);
        const minuteMatch = getMinutes(dateCur) === getMinutes(dateAcc);
        const toleranceMatch = cur.tolerance === acc.tolerance;
        if (hourMatch && minuteMatch && toleranceMatch) {
          return acc;
        }
        return false;
      }, null);
      return result === false ? 'advanced' : 'simple';
    },
    isEmptyOrUndefined(value) {
      return value === '' || value === undefined;
    },
    createAlarm() {
      const intervals = this.createScheduleForDays();
      const payload = {
        ...this.form,
        schedule: {
          intervals,
        },
      };
      if (this.isEditing) {
        this.$Alert.api()
          .update(this.$route.params.id, payload).then(() => {
            this.$router.push({ name: 'group-notifications' });
          });
      } else {
        this.$Alert.api()
          .create(this.groupName, payload).then(() => {
            this.$router.push({ name: 'group-notifications' });
          });
      }
    },
    createScheduleForDays() {
      const timestampsToAdd = [];
      if (this.scheduleType === 'simple') {
        const selectedDays = Object.keys(this.planSpecificDays).filter((day) => this.planSpecificDays[day]);
        selectedDays.forEach((day) => {
          const currentDayTimes = this.changeTimeToDay(this.times, day);
          timestampsToAdd.push(this.createIntervalObject(currentDayTimes.start, currentDayTimes.end));
        });
      }
      if (this.scheduleType === 'advanced') {
        this.advancedTimes.forEach((day) => {
          day.times.forEach((time) => {
            const currentDayTimes = this.changeTimeToDay(time, day.day);
            timestampsToAdd.push(this.createIntervalObject(currentDayTimes.start, currentDayTimes.end));
          });
        });
      }
      return timestampsToAdd;
    },
    changeTimeToDay(time, day) {
      const changedDate = {
        start: setDay(this.currentTime, mappedDays[day]),
        end: setDay(this.currentTime, mappedDays[day]),
      };
      return {
        start: this.setTimeOnDate(changedDate.start, time.start),
        end: this.setTimeOnDate(changedDate.end, time.end),
      };
    },
    setTimeOnDate(date, time) {
      const [hours, minutes] = time.split(':');
      return setSeconds(setMinutes(setHours(date, hours), minutes), 0);
    },
    getStartTime(interval) {
      const timestamp = interval.timestamp - interval.tolerance;
      const date = fromUnixTime(timestamp);
      return format(date, 'HH:mm');
    },
    getEndTime(interval) {
      const timestamp = interval.timestamp + interval.tolerance;
      const date = fromUnixTime(timestamp);
      return format(date, 'HH:mm');
    },
    getMedianTime(from, to) {
      const fromUnix = getUnixTime(from);
      const toUnix = getUnixTime(to);
      return (fromUnix + toUnix) / 2;
    },
    getTolerance(from, to) {
      const fromUnix = getUnixTime(from);
      const toUnix = getUnixTime(to);
      return (toUnix - fromUnix) / 2;
    },
    createIntervalObject(from, to) {
      return {
        interval: 604800, // 1 week
        timestamp: this.getMedianTime(from, to),
        tolerance: this.getTolerance(from, to),
      };
    },
  },
};
</script>

<style scoped lang="scss">
.page {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.phone-numbers {
  font-size: 18px;
  font-weight: bold;
  color: #707070;
}

.content-section {
  background-color: white;
  flex: 1;
}

.plan-explainer {
  color: #707070;
}

.frequency-item {
  border: 1px solid $primary;
  border-radius: 4px;
  padding: 8px 24px;
  margin: 0px 4px;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    background-color: rgba(100, 132, 244, 0.3);
  }

  &-selected {
    color: #fff;
    background-color: $primary;

    &:hover {
      background-color: $primary;
    }
  }
}

.advanced-day {
  font-weight: 400;
  color: #aaa;

  &--active {
    font-weight: 500;
    color: $primary;
  }
}

.advanced-times-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.textfield-small ::v-deep {
  .v-text-field__slot, fieldset {
    height: 36px;
  }
  height: 32px;
}
</style>
